import axios from 'axios';

import {
    CART_ADD_ITEM,
    CART_ADD_ITEM_FAIL,
    CART_REMOVE_ITEM,
    CART_SAVE_SHIPPING_ADDRESS,
    CART_SAVE_PAYMENT_METHOD,
    CART_SAVE_CUSTOMIZATION,
    CART_SAVE_VOUCHER,
    CART_REMOVE_VOUCHER
} from '../constants/cartConstants';
import { BOOK_PRODUCT_ID, PRINT_PRODUCT_ID } from '../constants/productConfig';

// Generate unique cart item ID
const generateCartItemId = () => {
    return `cart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const addToCart = (id, qty = 1) => async (dispatch, getState) => {
    try {
        const { data } = await axios.get(`/api/products/${id}/`);
        
        // Create qty number of individual cart line items
        for (let i = 0; i < qty; i++) {
            dispatch({
                type: CART_ADD_ITEM,
                payload: {
                    cartItemId: generateCartItemId(),
                    product: data.ProductId,
                    name: data.name,
                    image: data.image,
                    price: parseFloat(data.price),  // Convert string to number
                    countInStock: data.count_in_stock,
                    is_digital: data.is_digital || false,  // Include digital flag
                    qty: 1  // Always 1 - each book is a separate line item
                }
            });
        }

        localStorage.setItem('cartItems', JSON.stringify(getState().cart.cartItems));
    } 
    catch (error) {
        console.error('Error adding item to cart:', error);
        dispatch({
            type: CART_ADD_ITEM_FAIL,
            payload: error.response && error.response.data.detail
                ? error.response.data.detail
                : error.message
        });
    }
};

// Add a product to cart with custom options (used by Book Configurator)
// Backward compatible signature:
//   addToCartWithOptions(id, customPrice, customName)
// New structured signature:
//   addToCartWithOptions(id, { customPrice, customName, qty, extraCopies, metadata })
export const addToCartWithOptions = (id, customPriceOrOptions, customNameArg) => async (dispatch, getState) => {
    try {
        const { data } = await axios.get(`/api/products/${id}/`);

        const options = (customPriceOrOptions && typeof customPriceOrOptions === 'object' && !Array.isArray(customPriceOrOptions))
            ? customPriceOrOptions
            : {
                customPrice: customPriceOrOptions,
                customName: customNameArg,
            };

        const itemQty = Number.isFinite(Number(options.qty))
            ? Math.max(1, parseInt(options.qty, 10))
            : 1;

        const isBaseBook = Number(id) === BOOK_PRODUCT_ID;
        const existingBaseBook = getState().cart.cartItems.some((item) => (
            Number(item.product) === BOOK_PRODUCT_ID || item?.metadata?.lineItemType === 'book_base'
        ));

        if (isBaseBook && existingBaseBook) {
            return;
        }

        dispatch({
            type: CART_ADD_ITEM,
            payload: {
                cartItemId: generateCartItemId(),
                product: data.ProductId,
                name: options.customName || data.name,
                image: Number(id) === PRINT_PRODUCT_ID ? '/site-assets/book-cover-flat.jpg' : data.image,
                price: options.customPrice !== undefined ? options.customPrice : parseFloat(data.price),
                countInStock: data.count_in_stock,
                is_digital: data.is_digital || false,
                qty: itemQty,
                extraCopies: Number.isFinite(Number(options.extraCopies))
                    ? Math.max(0, parseInt(options.extraCopies, 10))
                    : undefined,
                metadata: options.metadata && typeof options.metadata === 'object'
                    ? options.metadata
                    : undefined,
            }
        });
        localStorage.setItem('cartItems', JSON.stringify(getState().cart.cartItems));
    } catch (error) {
        dispatch({
            type: CART_ADD_ITEM_FAIL,
            payload: error.response && error.response.data.detail
                ? error.response.data.detail
                : error.message
        });
    }
};

export const removeFromCart = (cartItemId) => (dispatch, getState) => {
    dispatch({
        type: CART_REMOVE_ITEM,
        payload: cartItemId  // Now removes by unique cartItemId
    });
    localStorage.setItem('cartItems', JSON.stringify(getState().cart.cartItems));
};


export const saveShippingAddress = (data) => (dispatch) => {
    dispatch({
        type: CART_SAVE_SHIPPING_ADDRESS,
        payload: data
    });
    localStorage.setItem('shippingAddress', JSON.stringify(data));
};

export const savePaymentMethod = (data) => (dispatch) => {
    dispatch({
        type: CART_SAVE_PAYMENT_METHOD,
        payload: data
    });
    localStorage.setItem('paymentMethod', JSON.stringify(data));
};

export const saveCustomization = (data) => (dispatch) => {
    dispatch({
        type: CART_SAVE_CUSTOMIZATION,
        payload: data
    });
    localStorage.setItem('customization', JSON.stringify(data));
};

export const saveVoucher = (code, discountAmount, shippingDiscountAmount = 0, freeShippingApplied = false) => (dispatch) => {
    dispatch({
        type: CART_SAVE_VOUCHER,
        payload: { code, discountAmount, shippingDiscountAmount, freeShippingApplied }
    });
    localStorage.setItem('voucherCode', code);
    localStorage.setItem('discountAmount', discountAmount.toString());
    localStorage.setItem('shippingDiscountAmount', shippingDiscountAmount.toString());
    localStorage.setItem('freeShippingApplied', freeShippingApplied ? '1' : '0');
};

export const removeVoucherFromCart = () => (dispatch) => {
    dispatch({
        type: CART_REMOVE_VOUCHER
    });
    localStorage.removeItem('voucherCode');
    localStorage.removeItem('discountAmount');
    localStorage.removeItem('shippingDiscountAmount');
    localStorage.removeItem('freeShippingApplied');
};